// Gallery.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// === IMAGES ===
// Update these imports with your actual Japan images
import galleryHeroImage from '../assets/Japan/pexels-sunhung94-25172743.jpg';

import shibuya from '../assets/Japan/jezael-melgoza-CudNrzbsyZw-unsplash.jpg';           // Add your images
import asakusa from '../assets/Japan/1.jpg';
import skytree from '../assets/Japan/images (2).jpeg';
import mtfuji from '../assets/Japan/100462016.jpeg';
import lakeKawaguchi from '../assets/Japan/images (3).jpeg';
import hakoneRopeway from '../assets/Japan/hakone-loop_7.jpg';
import fushimiInari from '../assets/Japan/images (4).jpeg';
import arashiyama from '../assets/Japan/arashiyama-bambusy.jpg';
import kinkakuji from '../assets/Japan/100559212.jpeg';
import osakaCastle from '../assets/Japan/Osaka-Castle-Spring-2.jpg';
import dotonbori from '../assets/Japan/osaka-dotonbori-iStock-1138049211-1024x683.jpg';
import naraPark from '../assets/Japan/thumb_11df8824-b80e-475a-8f63-37d78f3e92b7.jpg';
import todaiji from '../assets/Japan/4100_11.jpg';
import hiroshimaPeace from '../assets/Japan/8385d460-4716-47bb-b0ce-dd421ee8a21f_cx0_cy2_cw0_w1080_h608_s.avif';
import miyajima from '../assets/Japan/1_Miyajima.jpg';
import nikko from '../assets/Japan/nikko-japan-travel-photo-20231114160027633-main-image.jpg';
import shirakawago from '../assets/Japan/shirakawago_1.jpg';
import himeji from '../assets/Japan/images (5).jpeg';
import universal from '../assets/Japan/images (6).jpeg';
import teamlab from '../assets/Japan/tokyo-teamlab-borderless-wins-2025-global-tourism-prize-244027.webp';

const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
};

const Gallery = () => {
    const { t } = useTranslation('gallery');
    const [index, setIndex] = useState(-1);

    // === 20 JAPAN PLACES ===
    const japanPlaces = [
        { image: shibuya, label: t('shibuya') },
        { image: asakusa, label: t('asakusa') },
        { image: skytree, label: t('skytree') },
        { image: mtfuji, label: t('mtfuji') },
        { image: lakeKawaguchi, label: t('lakeKawaguchi') },
        { image: hakoneRopeway, label: t('hakoneRopeway') },
        { image: fushimiInari, label: t('fushimiInari') },
        { image: arashiyama, label: t('arashiyama') },
        { image: kinkakuji, label: t('kinkakuji') },
        { image: osakaCastle, label: t('osakaCastle') },
        { image: dotonbori, label: t('dotonbori') },
        { image: naraPark, label: t('naraPark') },
        { image: todaiji, label: t('todaiji') },
        { image: hiroshimaPeace, label: t('hiroshimaPeace') },
        { image: miyajima, label: t('miyajima') },
        { image: nikko, label: t('nikko') },
        { image: shirakawago, label: t('shirakawago') },
        { image: himeji, label: t('himeji') },
        { image: universal, label: t('universal') },
        { image: teamlab, label: t('teamlab') },
    ];

    const slides = japanPlaces.map(item => ({
        src: item.image,
        width: 1600,
        height: 1067,
        alt: item.label,
        title: item.label,
    }));

    return (
        <div className="min-h-screen bg-[#07111f] text-white">
            {/* HERO SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative overflow-hidden"
            >
                <img
                    src={galleryHeroImage}
                    alt={t('heroTitle')}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#07111f]/90 via-[#07111f]/75 to-[#07111f]/40" />

                <div className="relative mx-auto flex min-h-[78vh] max-w-7xl items-center px-6 py-24 sm:px-10 lg:px-16">
                    <div className="max-w-3xl">
                        <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-[#f7d774]">
                            {t('tabPlaces')}
                        </p>
                        <h1 className="font-serif text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
                            <span className="block text-[#7fd3ff]">{t('heroTitle')}</span>
                            <span className="mt-3 block text-white">{t('tabPlaces')}</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-lg leading-8 text-white/90 sm:text-xl">
                            {t('heroSubtitle')}
                        </p>
                    </div>
                </div>
            </motion.section>

            {/* GALLERY CONTENT */}
            <section className="mx-auto container px-6 py-20 sm:px-10 lg:px-16">
                <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                    <div className="max-w-3xl">
                        <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#7fd3ff]">
                            {t('tabPlaces')}
                        </p>
                        <h2 className="mt-4 font-serif text-3xl font-semibold text-white sm:text-4xl">
                            {t('heroTitle')}
                        </h2>
                        <p className="mt-5 text-lg leading-8 text-white/80">
                            {t('heroSubtitle')}
                        </p>
                    </div>

                    <div className="flex items-center gap-4 rounded-full border border-white/10 bg-[#0f1d33]/80 px-4 py-3 shadow-[0_20px_50px_rgba(0,0,0,0.25)]">
                        <Link to="/gallery" className="border-b-2 border-[#7fd3ff] pb-1 text-sm font-semibold uppercase tracking-[0.25em] text-[#7fd3ff]">
                            {t('tabPlaces')}
                        </Link>
                        <span className="text-white/40">/</span>
                        <Link to="/events" className="pb-1 text-sm font-semibold uppercase tracking-[0.25em] text-white/70 transition hover:text-[#f7d774]">
                            {t('tabEvents')}
                        </Link>
                    </div>
                </div>

                <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8 xl:grid-cols-5">
                    {japanPlaces.map((item, i) => (
                        <motion.button
                            key={i}
                            whileHover={{ scale: 1.04, y: -8 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => setIndex(i)}
                            className="group relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)]"
                        >
                            <img
                                src={item.image}
                                alt={item.label}
                                className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-110 md:h-64 lg:h-72"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#07111f]/90 via-[#07111f]/30 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                            <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                                <h3 className="text-sm font-semibold uppercase tracking-[0.25em] text-[#f7d774] drop-shadow-xl md:text-base">
                                    {item.label}
                                </h3>
                            </div>
                        </motion.button>
                    ))}
                </div>
            </section>

            {/* LIGHTBOX */}
            <Lightbox
                open={index >= 0}
                close={() => setIndex(-1)}
                index={index}
                slides={slides}
                plugins={[Thumbnails, Zoom]}
                thumbnails={{ position: "bottom", width: 110, height: 80, border: 3, borderRadius: 12, padding: 6, gap: 16, imageFit: "cover" }}
                zoom={{ maxZoomPixelRatio: 4 }}
                styles={{
                    container: { backgroundColor: "rgba(7, 17, 31, 0.98)" },
                    thumbnail: { cursor: "pointer", background: "linear-gradient(45deg, #7fd3ff, #f7d774)", borderRadius: "12px", overflow: "hidden" },
                }}
                labels={{
                    close: t('lightboxClose'),
                    next: t('lightboxNext'),
                    previous: t('lightboxPrevious'),
                }}
            />
        </div>
    );
};

export default Gallery;